import { useState, useEffect, useCallback } from 'react';
import { Card } from '../types/memory-game.types';
import { GameStatus } from '@/contexts/games/types';
import { getRandomUnmatchedPair } from '../utils/card-generator';

interface UseHintSystemProps {
  cards: Card[];
  hintsRemaining: number;
  gameStatus: GameStatus;
  onHintUsed: () => void;
}

interface UseHintSystemReturn {
  highlightedCards: number[];
  showHint: () => boolean;
  clearHighlight: () => void;
}

export function useHintSystem({
  cards,
  hintsRemaining,
  gameStatus,
  onHintUsed
}: UseHintSystemProps): UseHintSystemReturn {
  const [highlightedCards, setHighlightedCards] = useState<number[]>([]);

  // Show hint — highlight stays until a highlighted card is clicked
  const showHint = useCallback((): boolean => {
    if (hintsRemaining <= 0 || gameStatus !== 'playing') {
      return false;
    }

    const unmatchedPair = getRandomUnmatchedPair(cards);
    if (unmatchedPair.length === 0) {
      return false;
    }

    setHighlightedCards(unmatchedPair.map(card => card.id));
    onHintUsed();
    return true;
  }, [cards, hintsRemaining, gameStatus, onHintUsed]);

  // Clear highlight (called when player clicks a highlighted card)
  const clearHighlight = useCallback(() => {
    setHighlightedCards([]);
  }, []);

  // Clear when game stops
  useEffect(() => {
    if (gameStatus !== 'playing') {
      setHighlightedCards([]);
    }
  }, [gameStatus]);

  return {
    highlightedCards,
    showHint,
    clearHighlight
  };
}
